import actionCreator from 'helpers/actionCreator';
import endpoints from 'services/endpoints';
import type { CancelToken } from 'types/indexTS';
import { PageFeedCommentsActionTypes } from './actionTypes';
import { DEFAULT_PAGE_FEED_COMMENTS_LIMIT } from './constants';
import { getNormalizedPageFeedCommentFromResponse, getPageFeedCommentFromResponse } from './normalizer';
import { pageFeedComentsSelectors } from './selectors';
import {
  CreateFeedCommentParameters,
  CreatePageFeedComment,
  CREATE_PAGE_FEED_COMMENT_ADDITIONAL_DATA,
  CREATE_PAGE_FEED_COMMENT_ERROR_ACTION,
  CREATE_PAGE_FEED_COMMENT_REQUEST_ACTION,
  CREATE_PAGE_FEED_COMMENT_SUCCESS_ACTION,
  DeletePageFeedComment,
  DELETE_PAGE_FEED_COMMENT_ADDITIONAL_DATA,
  DELETE_PAGE_FEED_COMMENT_ERROR_ACTION,
  DELETE_PAGE_FEED_COMMENT_REQUEST_ACTION,
  DELETE_PAGE_FEED_COMMENT_SUCCESS_ACTION,
  FetchPageFeedComments,
  FetchPageFeedNextComments,
  FETCH_NEXT_PAGE_FEED_COMMENT_ERROR_ACTION,
  FETCH_NEXT_PAGE_FEED_COMMENT_REQUEST_ACTION,
  FETCH_NEXT_PAGE_FEED_COMMENT_SUCCESS_ACTION,
  FETCH_PAGE_FEED_COMMENT_ERROR_ACTION,
  FETCH_PAGE_FEED_COMMENT_REQUEST_ACTION,
  FETCH_PAGE_FEED_COMMENT_SUCCESS_ACTION,
  LikePageFeedComment,
  LIKE_ADDITIONAL_DATA,
  LIKE_PAGE_FEED_COMMENT_ERROR_ACTION,
  LIKE_PAGE_FEED_COMMENT_REQUEST_ACTION,
  LIKE_PAGE_FEED_COMMENT_SUCCESS_ACTION,
  PageFeedCommentsDeleteParams,
  PageFeedCommentsLikeParams,
  PageFeedCommentsNextQuery,
  PageFeedCommentsQuery,
} from './types';

// ----- Fetch Page Feed Comments -----
const fetchPageFeedCommentsSuccess = ({ data, parameters, additionalData }): FETCH_PAGE_FEED_COMMENT_SUCCESS_ACTION =>
  actionCreator.createAction(
    PageFeedCommentsActionTypes.FETCH_PAGE_FEED_COMMENT_SUCCESS,
    {
      data: getNormalizedPageFeedCommentFromResponse(data.data),
      pagination: data.pagination,
      query: parameters,
    },
    additionalData,
  );

const fetchPageFeedCommentsError = ({ additionalData, errorData }): FETCH_PAGE_FEED_COMMENT_ERROR_ACTION =>
  actionCreator.createErrorAction(
    PageFeedCommentsActionTypes.FETCH_PAGE_FEED_COMMENT_ERROR,
    errorData,
    {},
    additionalData,
  );

const fetchPageFeedCommentsRequest = (
  feedId: string,
  query: PageFeedCommentsQuery,
  resetLoadedData: boolean | undefined,
  cancelToken: CancelToken,
): FETCH_PAGE_FEED_COMMENT_REQUEST_ACTION =>
  actionCreator.createAction(PageFeedCommentsActionTypes.FETCH_PAGE_FEED_COMMENT_REQUEST, query, {
    cancelToken,
    query,
    resetLoadedData,
    feedId,
  });

const fetchPageFeedComments: FetchPageFeedComments = ({
  slug,
  feedId,
  limit = DEFAULT_PAGE_FEED_COMMENTS_LIMIT,
}: PageFeedCommentsQuery) => {
  const additionalData = { slug, feedId, limit };
  return actionCreator.createAsyncAction({
    additionalData,
    endpoint: endpoints.PAGE_FEED_COMMENTS.GET,
    errorBinding: fetchPageFeedCommentsError,
    loadingAction: (cancelToken: CancelToken) =>
      fetchPageFeedCommentsRequest(feedId, { slug, limit, feedId }, false, cancelToken),
    method: 'get',
    parameters: { limit },
    urlParameters: {
      slug,
      feedId,
    },
    successBinding: fetchPageFeedCommentsSuccess,
    cancelTokenSelector: state => pageFeedComentsSelectors.fetchingCancelToken(state, feedId),
  });
};

// ----- Fetch Page Feed Next Comments -----
const fetchPageFeedNextCommentsSuccess = ({
  data,
  parameters,
  additionalData,
}): FETCH_NEXT_PAGE_FEED_COMMENT_SUCCESS_ACTION =>
  actionCreator.createAction(
    PageFeedCommentsActionTypes.FETCH_NEXT_PAGE_FEED_COMMENT_SUCCESS,
    {
      data: getNormalizedPageFeedCommentFromResponse(data.data),
      pagination: data.pagination,
      query: parameters,
    },
    additionalData,
  );

const fetchPageFeedNextCommentsError = ({ additionalData, errorData }): FETCH_NEXT_PAGE_FEED_COMMENT_ERROR_ACTION =>
  actionCreator.createErrorAction(
    PageFeedCommentsActionTypes.FETCH_NEXT_PAGE_FEED_COMMENT_ERROR,
    errorData,
    {},
    additionalData,
  );

const fetchPageFeedNextCommentsRequest = (
  feedId: string,
  query: PageFeedCommentsNextQuery,
  resetLoadedData: boolean | undefined,
  cancelToken: CancelToken,
): FETCH_NEXT_PAGE_FEED_COMMENT_REQUEST_ACTION =>
  actionCreator.createAction(PageFeedCommentsActionTypes.FETCH_NEXT_PAGE_FEED_COMMENT_REQUEST, query, {
    cancelToken,
    query,
    resetLoadedData,
    feedId,
  });

const fetchPageFeedNextComments: FetchPageFeedNextComments = (feedId: string, nextUrl: string) => {
  const additionalData = { feedId };
  return actionCreator.createAsyncAction({
    additionalData,
    absoluteEndPoint: nextUrl,
    errorBinding: fetchPageFeedNextCommentsError,
    loadingAction: (cancelToken: CancelToken) =>
      fetchPageFeedNextCommentsRequest(feedId, { feedId }, false, cancelToken),
    method: 'get',
    successBinding: fetchPageFeedNextCommentsSuccess,
    cancelTokenSelector: state => pageFeedComentsSelectors.fetchingCancelToken(state, feedId),
  });
};

// ----- Fetch Page Feed Create Comment -----
const createPageFeedCommentSuccess = ({ data, additionalData }): CREATE_PAGE_FEED_COMMENT_SUCCESS_ACTION =>
  actionCreator.createAction(
    PageFeedCommentsActionTypes.CREATE_PAGE_FEED_COMMENT_SUCCESS,
    getPageFeedCommentFromResponse(data),
    additionalData,
  );

const createPageFeedCommentError = ({ additionalData, errorData }): CREATE_PAGE_FEED_COMMENT_ERROR_ACTION =>
  actionCreator.createErrorAction(
    PageFeedCommentsActionTypes.CREATE_PAGE_FEED_COMMENT_ERROR,
    errorData,
    {},
    additionalData,
  );

const createPageFeedCommentRequest = (
  feedId: string,
  parameters: CreateFeedCommentParameters,
): CREATE_PAGE_FEED_COMMENT_REQUEST_ACTION =>
  actionCreator.createAction(PageFeedCommentsActionTypes.CREATE_PAGE_FEED_COMMENT_REQUEST, parameters, { feedId });

const createPageFeedComment: CreatePageFeedComment = (
  slug: string,
  feedId: string,
  parameters: CreateFeedCommentParameters,
) => {
  const additionalData: CREATE_PAGE_FEED_COMMENT_ADDITIONAL_DATA = { feedId };
  return actionCreator.createAsyncAction({
    additionalData,
    endpoint: endpoints.PAGE_FEED_COMMENTS.CREATE,
    errorBinding: createPageFeedCommentError,
    loadingAction: () => createPageFeedCommentRequest(feedId, parameters),
    method: 'post',
    parameters,
    urlParameters: {
      slug,
      feedId,
    },
    successBinding: createPageFeedCommentSuccess,
  });
};

// ----- Fetch Page Feed Delete Comment -----
const deletePageFeedCommentSuccess = ({ additionalData }): DELETE_PAGE_FEED_COMMENT_SUCCESS_ACTION =>
  actionCreator.createAction(PageFeedCommentsActionTypes.DELETE_PAGE_FEED_COMMENT_SUCCESS, {}, additionalData);

const deletePageFeedCommentError = ({ additionalData, errorData }): DELETE_PAGE_FEED_COMMENT_ERROR_ACTION =>
  actionCreator.createErrorAction(
    PageFeedCommentsActionTypes.DELETE_PAGE_FEED_COMMENT_ERROR,
    errorData,
    {},
    additionalData,
  );

const deletePageFeedCommentRequest = (
  urlParameters: PageFeedCommentsDeleteParams,
): DELETE_PAGE_FEED_COMMENT_REQUEST_ACTION =>
  actionCreator.createAction(PageFeedCommentsActionTypes.DELETE_PAGE_FEED_COMMENT_REQUEST, {}, urlParameters);

const deletePageFeedComment: DeletePageFeedComment = (urlParameters: PageFeedCommentsDeleteParams) => {
  const additionalData: DELETE_PAGE_FEED_COMMENT_ADDITIONAL_DATA = {
    feedId: urlParameters.feedId,
    feedCommentId: urlParameters.feedCommentId,
  };
  return actionCreator.createAsyncAction({
    additionalData,
    endpoint: endpoints.PAGE_FEED_COMMENTS.DELETE,
    errorBinding: deletePageFeedCommentError,
    loadingAction: () => deletePageFeedCommentRequest(urlParameters),
    method: 'delete',
    urlParameters,
    successBinding: deletePageFeedCommentSuccess,
  });
};

// ----- Fetch Page Feed Like Comment -----
const likePageFeedCommentSuccess = ({ additionalData }): LIKE_PAGE_FEED_COMMENT_SUCCESS_ACTION =>
  actionCreator.createAction(PageFeedCommentsActionTypes.LIKE_PAGE_FEED_COMMENT_SUCCESS, {}, additionalData);

const likePageFeedCommentError = ({ additionalData, errorData }): LIKE_PAGE_FEED_COMMENT_ERROR_ACTION =>
  actionCreator.createErrorAction(
    PageFeedCommentsActionTypes.LIKE_PAGE_FEED_COMMENT_ERROR,
    errorData,
    {},
    additionalData,
  );

const likePageFeedCommentRequest = (
  parameters: PageFeedCommentsLikeParams,
  additionalData: LIKE_ADDITIONAL_DATA,
): LIKE_PAGE_FEED_COMMENT_REQUEST_ACTION =>
  actionCreator.createAction(PageFeedCommentsActionTypes.LIKE_PAGE_FEED_COMMENT_REQUEST, parameters, additionalData);

const likePageFeedComment: LikePageFeedComment = (parameters: PageFeedCommentsLikeParams) => {
  const additionalData: LIKE_ADDITIONAL_DATA = {
    feedCommentId: parameters.feedCommentId,
    liked: parameters.liked,
    feedId: parameters.feedId,
  };
  return actionCreator.createAsyncAction({
    additionalData,
    endpoint: endpoints.PAGE_FEED_COMMENTS.LIKE,
    errorBinding: likePageFeedCommentError,
    loadingAction: () => likePageFeedCommentRequest(parameters, additionalData),
    method: 'post',
    urlParameters: {
      slug: parameters.slug,
      feedId: parameters.feedId,
      feedCommentId: parameters.feedCommentId,
    },
    parameters: {
      liked: parameters.liked,
    },
    successBinding: likePageFeedCommentSuccess,
  });
};

// ----- Exported actions -----
export const pageFeedCommentsActions = {
  fetchPageFeedComments,
  fetchPageFeedNextComments,
  createPageFeedComment,
  deletePageFeedComment,
  likePageFeedComment,
};
